import { useMemo } from "react";
import {
  Box, Card, CardContent, Stack, Typography, LinearProgress, useTheme, alpha,
} from "@mui/material";
import { DonutLargeOutlined } from "@mui/icons-material";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";
import { STAGE_ORDER, STAGE_DEFS, type StageKey } from "../../utils/transactionStage";
import { colorForStage, type TimelineResult } from "./stageTimelineDrill";

// "In QBD" is the terminal success state; everything else is still open / unmatched.
// Terminal stages excluded from "open work": posted (in_qbd) + skipped ($0/info).
const TERMINAL_STAGES: ReadonlySet<StageKey> = new Set<StageKey>(["in_qbd", "skipped"]);

type Slice = { key: StageKey; label: string; value: number; color: string; pct: number };

export function StageUnmatchedDonut({ res }: { res: TimelineResult | undefined }) {
  const theme = useTheme();

  // Aggregate every bucket in the current selection into one open-by-stage split.
  const { slices, total } = useMemo<{ slices: Slice[]; total: number }>(() => {
    if (!res) return { slices: [], total: 0 };
    const sums = {} as Record<StageKey, number>;
    for (const k of STAGE_ORDER) sums[k] = 0;
    for (const b of res.buckets) for (const k of STAGE_ORDER) sums[k] += b.counts[k] ?? 0;
    const open = STAGE_ORDER.filter((k) => !TERMINAL_STAGES.has(k) && sums[k] > 0);
    const total = open.reduce((s, k) => s + sums[k], 0);
    const slices = open
      .map((k) => ({
        key: k,
        label: STAGE_DEFS[k].label,
        value: sums[k],
        color: colorForStage(k, theme.palette),
        pct: total > 0 ? (sums[k] / total) * 100 : 0,
      }))
      .sort((a, b) => b.value - a.value);
    return { slices, total };
  }, [res, theme.palette]);

  const cardSx = {
    background: `linear-gradient(135deg, ${alpha("#6c5ce7", 0.04)}, ${alpha("#00cec9", 0.03)})`,
    border: `1px solid ${theme.palette.divider}`,
    height: "100%",
    position: "relative" as const,
    overflow: "hidden" as const,
  };

  const header = (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
      <DonutLargeOutlined sx={{ color: "var(--c-link-accent)" }} />
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          Open work by Stage
        </Typography>
        <Typography variant="caption" color="text.secondary">
          % of transactions not yet in QuickBooks
        </Typography>
      </Box>
    </Stack>
  );

  if (!res) {
    return (
      <Card sx={cardSx}>
        <CardContent sx={{ p: 3 }}>
          {header}
          <LinearProgress sx={{ height: 3, borderRadius: 2 }} />
        </CardContent>
      </Card>
    );
  }

  const DonutTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const s: Slice = payload[0]?.payload;
    if (!s) return null;
    return (
      <Box sx={{ px: 1.5, py: 0.75, borderRadius: 1.5, bgcolor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, boxShadow: "0 8px 28px rgba(0,0,0,0.18)" }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: s.color }} />
          <Typography variant="caption" sx={{ fontWeight: 600 }}>{s.label}</Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">{s.value} · {s.pct.toFixed(1)}%</Typography>
      </Box>
    );
  };

  return (
    <Card sx={cardSx}>
      <CardContent sx={{ p: 3 }}>
        {header}

        {total === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 6 }}>
            All caught up — everything in this period is in QuickBooks.
          </Typography>
        ) : (
          <>
            <Box sx={{ position: "relative", width: "100%", height: 200, mt: 1 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={slices}
                    dataKey="value"
                    nameKey="label"
                    innerRadius={58}
                    outerRadius={84}
                    paddingAngle={1.5}
                    stroke="none"
                    isAnimationActive
                  >
                    {slices.map((s) => (
                      <Cell key={s.key} fill={s.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<DonutTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              {/* center label */}
              <Box
                sx={{
                  position: "absolute", inset: 0, display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center", pointerEvents: "none",
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1 }}>{total}</Typography>
                <Typography variant="caption" color="text.secondary">open</Typography>
              </Box>
            </Box>

            {/* legend with % — sorted desc */}
            <Stack spacing={0.5} sx={{ mt: 2 }}>
              {slices.map((s) => (
                <Stack key={s.key} direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
                    <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: s.color, flexShrink: 0 }} />
                    <Typography variant="caption" noWrap>{s.label}</Typography>
                  </Stack>
                  <Typography variant="caption" sx={{ fontWeight: 700, flexShrink: 0 }}>
                    {s.pct.toFixed(0)}% <Box component="span" sx={{ color: "text.secondary", fontWeight: 400 }}>({s.value})</Box>
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
}
